// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable formatjs/no-literal-string-in-jsx */
import useMemberQuery from "#/services/user/member-info"
import { useUserRole } from "#/components/partner-access/hooks/usePartnerAccess"
import { PageLayout } from "#/features/global-ui/page-layout"
import { Title } from "#/services/page-title/Title"
import Settings from "#/utils/CarrotConfig"
import { DbgPaths, Paths } from "#/utils/Paths"
import { TelemedicineFlowErrorBoundary } from "#/pages/telemedicine/TelemedicineFlowErrorBoundary"
import { BookingFlow, useTelemedicineAccess } from "@internal/telemedicine"
import { BookingEntryPayment, parseBookingEntryPayment } from "@internal/constants"
import { useBookingFlowTracking } from "#/services/memberActionTracking/BookingTrackingProvider"
import { useGetPhoneSupportNumberForUser } from "#/components/dynamic-content/hooks/useQueryPhoneSupport"
import { useMemberProgramFlags } from "#/services/user/hooks/useMemberProgram"
import React from "react"
import { useFlags } from "launchdarkly-react-client-sdk"
import { Redirect, useHistory, useLocation } from "react-router-dom"

// The raw `?program=` value, or null when absent. Not validated here — the backend decides which
// programs the member may book (see useTelemedicineAccess).
function useProgramParam(): string | null {
  const { search } = useLocation()
  const raw = new URLSearchParams(search).get("program")
  return raw === null || raw === "" ? null : raw
}

export function TelemedicineBookingPage(): JSX.Element | null {
  const { telemedicineEnterprisePayments } = useFlags()
  const history = useHistory()
  const program = useProgramParam()
  const { state, search } = useLocation<{ returnTo?: string } | undefined>()
  const paymentUrlParam = new URLSearchParams(search).get("payment")
  const payment = parseBookingEntryPayment(paymentUrlParam)
  const isPaymentMissing = payment === undefined
  // Member type determines payment availability and the missing-payment fallback:
  // Enterprise members are redirected to Carrot Clinic; Consumer members continue booking.
  const shouldLoadMemberProgram = telemedicineEnterprisePayments === true || isPaymentMissing
  const memberProgram = useMemberProgramFlags(shouldLoadMemberProgram)
  const isEnterpriseMember = memberProgram.data?.isConsumer === false
  // The URL supplies initial intent; the machine owns subsequent navigation.
  const insuranceEntry = payment === BookingEntryPayment.Insurance
  const enterprisePaymentsEnabled = telemedicineEnterprisePayments === true && isEnterpriseMember
  const returnTo = state?.returnTo
  const { data: access, isLoading, isError } = useTelemedicineAccess()
  // The legal name and date of birth of whoever is booking, for the HIPAA "My name:" line and the
  // insurance check (see BookingFlow). A signed-in partner books as themselves, not as the member.
  const { isPartnerAccessingAccount, isUserRoleLoading, isUserRoleError } = useUserRole()
  // The partner fallbacks below are safe only once partners are gated on entering their name and date of birth
  // before booking, and that gate must invalidate the ["member"] query after saving so this page sees the new values.
  const {
    data: booker,
    isLoading: isBookerLoading,
    isError: isBookerError
  } = useMemberQuery((user) => ({
    employeeId: user.employeeId,
    patient: isPartnerAccessingAccount
      ? {
          firstName: user.partnerFirstName ?? "",
          lastName: user.partnerLastName ?? "",
          dateOfBirth: user.partnerDateOfBirth ?? ""
        }
      : {
          firstName: user.firstName ?? "",
          lastName: user.lastName ?? "",
          dateOfBirth: user.dateOfBirth ?? ""
        }
  }))
  const { data: phoneSupport } = useGetPhoneSupportNumberForUser()
  const supportPhone = phoneSupport?.e164FormatPhoneNumber
    ? { display: phoneSupport.internationalFormatPhoneNumber, e164: phoneSupport.e164FormatPhoneNumber }
    : undefined

  // No program named → let the resolver decide.
  if (program === null) {
    return <Redirect to={{ pathname: Paths.TELEMEDICINE, search, state }} />
  }

  // A failed access read is anomalous at this entry point — send the member home rather than render a
  // blank page or the flow. The BFF is the authority on booking either way.
  if (isError || (shouldLoadMemberProgram && memberProgram.isError)) {
    return <Redirect to={Paths.HOME} />
  }

  // Without the role or the user record we can't tell who is booking, so send them home rather than book under the wrong name.
  if (isUserRoleError || isBookerError) {
    return <Redirect to={Paths.HOME} />
  }

  if (
    isLoading ||
    !access ||
    isUserRoleLoading ||
    isBookerLoading ||
    !booker ||
    (shouldLoadMemberProgram && !memberProgram.data)
  ) {
    return null
  }

  const { patient } = booker
  const memberName = [patient.firstName, patient.lastName].filter(Boolean).join(" ")

  // Gate on the member's actual access: the requested program must be one their benefit turns on AND
  // one they're qualified for. Unqualified members no longer get a booking link at all (MEM-817), so
  // reaching this URL while unqualified — or for a program that isn't theirs — is anomalous; send
  // them home. `?program=fertility` aliases to mens_fertility when that is the member's program.
  // UX gate only — the BFF re-enforces every booking procedure server-side.
  let match = access.programs.find((candidate) => candidate.program === program)
  if (!match && program === "fertility") {
    match = access.programs.find((candidate) => candidate.program === "mens_fertility")
  }
  if (!match || !match.qualified) {
    return <Redirect to={Paths.HOME} />
  }

  if (isPaymentMissing && isEnterpriseMember) {
    return <Redirect to={DbgPaths.CARROT_CLINIC} />
  }

  let entrySource: React.ComponentProps<typeof BookingFlow>["entrySource"] = "carrotPlan"
  if (returnTo === "/care-team") {
    entrySource = "careTeam"
  } else if (returnTo) {
    entrySource = "searchResults"
  }

  // Contains a render-time throw in the flow to this page instead of white-screening the whole app.
  return (
    <TelemedicineFlowErrorBoundary>
      <PageLayout variant="default">
        <Title title="Telemedicine Booking" />
        <TrackedBookingFlow
          key={`${booker.employeeId}:${match.program}`}
          bridgeConfig={
            Settings.BRIDGE_PUBLISHABLE_KEY && Settings.BRIDGE_ENVIRONMENT
              ? {
                  publishableKey: Settings.BRIDGE_PUBLISHABLE_KEY,
                  environment: Settings.BRIDGE_ENVIRONMENT
                }
              : undefined
          }
          insuranceEntry={insuranceEntry && enterprisePaymentsEnabled}
          program={match.program}
          memberName={memberName}
          insuranceMember={patient}
          enterprisePaymentsEnabled={enterprisePaymentsEnabled}
          supportPhone={supportPhone}
          entrySource={entrySource}
          onReturnToProviderResults={returnTo ? () => history.push(returnTo, state) : undefined}
          onExploreCareOptions={() => history.push(Paths.READ)}
          onBackToEntry={() =>
            returnTo
              ? history.push(returnTo, state)
              : window.history.length > 1
              ? history.goBack()
              : history.push(Paths.HOME)
          }
        />
      </PageLayout>
    </TelemedicineFlowErrorBoundary>
  )
}

function TrackedBookingFlow(props: React.ComponentProps<typeof BookingFlow>): JSX.Element {
  const tracking = useBookingFlowTracking(props.program)
  return <BookingFlow {...props} tracking={tracking} />
}
